export const normalize = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
export const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' });
const clouds = ['AWS', 'Azure', 'GCP', 'Huawei', 'OCI', 'Multicloud'];
const status = ['Verde', 'Amarillo', 'Rojo'];
const practices = ['SA', 'SP', 'Ambas'];
const field = (key, label, type = 'text', options = {}) => ({ key, label, type, ...options });
const base = [
  field('fecha', 'Fecha', 'date', { required: true }),
  field('cliente', 'Cliente', 'text', { required: true }),
  field('nube', 'Nube', 'select', { options: clouds, required: true }),
];
const owner = field('responsable', 'Responsable', 'text', { required: true });
const semaphore = field('semaforo', 'Estatus (semáforo)', 'select', { options: status, required: true });
const comments = field('comentarios', 'Comentarios', 'textarea');
const practice = field('practica', 'Práctica', 'select', { options: practices, required: true });
const service = field('servicio', 'Tipo de servicio', 'text', { required: true });
export const TABLES = {
  servicios: { label: 'Servicios Administrados', fields: [...base, service, owner, semaphore,
    field('tickets_sla', 'Tickets fuera de SLA', 'number'), field('incidentes', 'Incidentes mayores', 'number'),
    field('comentarios', 'Comentarios / Riesgos', 'textarea')] },
  proyectos: { label: 'Proyectos SP', fields: [...base, field('nombre', 'Proyecto', 'text', { required: true }),
    service, owner, field('fase', 'Fase', 'text', { required: true }), semaphore,
    field('avance', '% Avance planeado', 'number', { max: 100, step: 'any', percent: true }),
    field('inicio', 'Fecha inicio', 'date'), field('fin', 'Fecha fin planeada', 'date'),
    field('presupuesto', 'Total presupuestado ($)', 'number', { step: 'any' }),
    field('escalamiento', '¿Requiere escalamiento?', 'checkbox'), comments] },
  pipeline: { label: 'Pipeline', fields: [...base, field('nombre', 'Oportunidad', 'text', { required: true }),
    field('tipo', 'Tipo', 'text', { required: true }), practice,
    field('etapa', 'Etapa', 'select', { required: true, options: ['Prospecto', 'Calificacion', 'Propuesta', 'Negociacion', 'Ganada', 'Perdida'] }),
    field('monto', 'Monto SA/SP (USD/mes)', 'number', { step: 'any' }),
    field('cierre', 'Fecha cierre estimada', 'date'), field('preventa', 'Preventa', 'text', { required: true }),
    field('siguiente_paso', 'Siguiente paso', 'textarea'), comments] },
  temas: { label: 'Temas y riesgos', fields: [...base,
    field('tipo', 'Tipo', 'select', { required: true, options: ['Highlight', 'Riesgo', 'Bloqueo', 'Decision'] }),
    practice, field('descripcion', 'Descripción', 'textarea', { required: true }),
    field('impacto', 'Impacto', 'select', { required: true, options: ['Alto', 'Medio', 'Bajo'] }),
    field('mitigacion', 'Acción / Mitigación', 'textarea'), field('responsable', 'Dueño', 'text', { required: true }),
    field('compromiso', 'Fecha compromiso', 'date'),
    field('estatus', 'Estatus', 'select', { required: true, options: ['Abierto', 'En progreso', 'Cerrado'] }),
    field('direccion', '¿Mostrar a dirección?', 'checkbox')] },
};

export function formPayload(kind, form) {
  return Object.fromEntries(TABLES[kind].fields.map((f) => {
    const value = form[f.key];
    if (f.type === 'checkbox') return [f.key, Boolean(value)];
    if (value === '' || value === undefined || value === null) return [f.key, f.type === 'textarea' ? '' : null];
    return [f.key, f.type === 'number' ? Number(value) / (f.percent ? 100 : 1) : String(value).trim()];
  }));
}

export function buildReport(content, filters = {}) {
  const sections = Object.fromEntries(Object.entries(TABLES).map(([kind, table]) => [kind, {
    headers: table.fields.map((f) => f.label),
    rows: (content[kind] || []).filter((r) => r.activo
      && (!filters.cliente || normalize(r.datos.cliente).includes(normalize(filters.cliente)))
      && (!filters.nube || r.datos.nube === filters.nube)
      && (!filters.responsable || normalize(r.datos.responsable || r.datos.preventa).includes(normalize(filters.responsable))))
      .map((r) => ({
        id: r.id, date: r.datos.fecha,
        ...Object.fromEntries(table.fields.map((f) => [f.label,
          f.type === 'checkbox' ? (r.datos[f.key] ? 'Sí' : 'No') : r.datos[f.key]])),
      })),
  }]));
  const admin = sections.servicios.rows;
  const clients = new Map();
  const rank = { verde: 0, amarillo: 1, rojo: 2 };
  admin.forEach((row) => {
    const key = normalize(row.Cliente), current = clients.get(key), state = normalize(row['Estatus (semáforo)']);
    if (!current || rank[state] === undefined || (rank[current] !== undefined && rank[state] > rank[current])) clients.set(key, state);
  });
  const statuses = (states) => Object.fromEntries(['verde', 'amarillo', 'rojo', 'sin dato'].map((state) =>
    [state, states.filter((value) => state === 'sin dato' ? rank[value] === undefined : value === state).length]));
  const pipeline = sections.pipeline.rows.filter((row) => !['ganada', 'perdida'].includes(normalize(row.Etapa)));
  const groups = new Map();
  pipeline.forEach((row) => {
    const key = row.Práctica || 'Sin dato';
    groups.set(key, (groups.get(key) || 0) + (row['Monto SA/SP (USD/mes)'] ?? 0));
  });
  const sum = (rows, key) => rows.reduce((total, row) => total + (row[key] ?? 0), 0);
  const absent = (value) => value === null || value === undefined;
  return {
    sections, adminStatuses: statuses([...clients.values()]), clientCount: clients.size,
    projectStatuses: statuses(sections.proyectos.rows.map((r) => normalize(r['Estatus (semáforo)']))),
    projects: sections.proyectos.rows.length, pipeline,
    pipelineGroups: [...groups].map(([name, amount]) => ({ name, amount })),
    issues: sections.temas.rows.filter((r) => normalize(r['¿Mostrar a dirección?']) === 'si' && normalize(r.Estatus) !== 'cerrado'),
    pipelineAmount: sum(pipeline, 'Monto SA/SP (USD/mes)'),
    pipelineMissing: pipeline.filter((r) => absent(r['Monto SA/SP (USD/mes)'])).length,
    escalations: sections.proyectos.rows.filter((r) => normalize(r['¿Requiere escalamiento?']) === 'si').length,
    sla: sum(admin, 'Tickets fuera de SLA'), incidents: sum(admin, 'Incidentes mayores'),
    slaMissing: admin.filter((r) => absent(r['Tickets fuera de SLA']) || absent(r['Incidentes mayores'])).length,
    budget: sum(sections.proyectos.rows, 'Total presupuestado ($)'),
    budgetMissing: sections.proyectos.rows.filter((r) => absent(r['Total presupuestado ($)'])).length,
  };
}
